import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "react-bootstrap";
import { DropdownAtom } from "../atoms/atom";
import {
	normalizePermissions,
	permissionsEqual,
} from "../../constant/projectPermissions";
import {
	useListPresetsByTypeQuery,
} from "../../store/services/projectPermissionPresets";

function PermissionPresetSelector({
	projectId,
	value,
	onChange,
	onPresetApplied,
	labelKey = "projects.permissions.apply-preset",
	placeholderKey = "projects.permissions.select-preset",
	disabled = false,
	showModifiedIndicator = true,
	appliedPermissions = null,
	className = "",
}) {
	const { t } = useTranslation();
	const [selectedPresetId, setSelectedPresetId] = useState(value || "");
	const [presetModified, setPresetModified] = useState(false);

	const { data: systemData, isLoading: systemLoading } =
		useListPresetsByTypeQuery(
			{ projectId, type: "system" },
			{ skip: !projectId },
		);
	const { data: projectData, isLoading: projectLoading } =
		useListPresetsByTypeQuery(
			{ projectId, type: "project" },
			{ skip: !projectId },
		);

	const systemPresets = useMemo(
		() => systemData?.items ?? systemData?.system ?? [],
		[systemData],
	);
	const projectPresets = useMemo(
		() => projectData?.items ?? projectData?.project ?? [],
		[projectData],
	);

	const groupedOptions = useMemo(
		() => [
			{
				label: t("projects.permissions.preset-group-system"),
				options: systemPresets.map((preset) => ({
					value: preset.id,
					label: preset.name,
					preset,
				})),
			},
			{
				label: t("projects.permissions.preset-group-project"),
				options: projectPresets.map((preset) => ({
					value: preset.id,
					label: preset.name,
					preset,
				})),
			},
		],
		[systemPresets, projectPresets, t],
	);

	useEffect(() => {
		setSelectedPresetId(value || "");
	}, [value]);

	// Derive the selected preset from its id (not a separate state set only on
	// click) so the dropdown and the "modified" check stay correct even when the
	// selection is driven by the `value` prop from a parent.
	const selectedPreset = useMemo(
		() =>
			[...systemPresets, ...projectPresets].find(
				(p) => p.id === selectedPresetId,
			) || null,
		[systemPresets, projectPresets, selectedPresetId],
	);

	useEffect(() => {
		if (!showModifiedIndicator || !selectedPreset || !appliedPermissions) {
			setPresetModified(false);
			return;
		}
		setPresetModified(
			!permissionsEqual(selectedPreset.permissions, appliedPermissions),
		);
	}, [selectedPreset, appliedPermissions, showModifiedIndicator]);

	const handleSelect = (option) => {
		const preset = option?.preset;
		if (!preset) {
			setSelectedPresetId("");
			onChange?.(null);
			return;
		}
		setSelectedPresetId(preset.id);
		const normalized = normalizePermissions(preset.permissions);
		onChange?.(normalized);
		onPresetApplied?.({
			preset,
			presetUsedName: preset.name,
			permissions: normalized,
		});
	};

	const rootClass = ["permission-preset-selector", className]
		.filter(Boolean)
		.join(" ");

	return (
		<div className={rootClass}>
			<DropdownAtom
				label={t(labelKey)}
				value={selectedPresetId}
				options={groupedOptions}
				isGrouped
				onChange={(e) => {
					const id = e.target.value;
					const allPresets = [...systemPresets, ...projectPresets];
					const preset = allPresets.find((p) => p.id === id);
					handleSelect(preset ? { preset } : null);
				}}
				placeholder={t(placeholderKey)}
				disabled={disabled || systemLoading || projectLoading}
				sortOptions={false}
				className="mb-2"
			/>
			{showModifiedIndicator && presetModified && (
				<Alert variant="info" className="mt-2 py-2 mb-0">
					{t("projects.permissions.preset-modified")}
				</Alert>
			)}
		</div>
	);
}

export default PermissionPresetSelector;
